'use client'

import { useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { useSearchParams } from 'next/navigation'

import { firstNameOf } from '@/lib/name'
import { Z2H_DIMENSIONS, listLabels } from '@/lib/z2h/coverage'
import { useVapiInterview } from './useVapiInterview'

// WebGL blob — client-only, loaded lazily so the chat never waits on it.
const Orb = dynamic(() => import('@/components/ui/orb/Orb'), { ssr: false })

const OPENER =
  "Welcome — glad you're here. Let's start simple: tell me about yourself, then the venture you're building. What's the idea? What sparked it?"

function MicIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
      <rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" />
      <path
        d="M5 11a7 7 0 0 0 14 0M12 18v3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}

function MicOffIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
      <rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" />
      <path
        d="M5 11a7 7 0 0 0 14 0M12 18v3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M4.5 3.5l15 17"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  )
}

// Send glyphs in the house dot language. Idle shows a plain up arrow; once
// the founder types, the dots morph pixel-by-pixel into an ↵ enter arrow.
const SEND_IDLE = [
  [0, 0, 1, 0, 0],
  [0, 1, 1, 1, 0],
  [1, 0, 1, 0, 1],
  [0, 0, 1, 0, 0],
  [0, 0, 1, 0, 0],
]
const SEND_READY = [
  [0, 0, 0, 0, 1],
  [0, 0, 0, 0, 1],
  [0, 1, 0, 0, 1],
  [1, 1, 1, 1, 1],
  [0, 1, 0, 0, 0],
]

function SendDotsIcon({ ready }) {
  return (
    <span className="flex flex-col gap-px">
      {SEND_IDLE.map((row, r) => (
        <span key={r} className="flex gap-px">
          {row.map((_, c) => {
            const on = (ready ? SEND_READY : SEND_IDLE)[r][c]
            return (
              <span
                key={c}
                style={{
                  opacity: on ? 1 : 0.22,
                  transitionDelay: `${(r * 5 + c) * 12}ms`,
                }}
                className="inline-block size-0.75 shrink-0 rounded-full bg-current transition-opacity duration-300"
              />
            )
          })}
        </span>
      ))}
    </span>
  )
}

// Voice conversation icon in the house dot language: a pixel-dot waveform,
// columns rising toward the center like speech.
function DotWaveIcon({ className }) {
  const columns = [
    { x: 4, dots: 2 },
    { x: 8, dots: 4 },
    { x: 12, dots: 5 },
    { x: 16, dots: 3 },
    { x: 20, dots: 2 },
  ]
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      {columns.flatMap(({ x, dots }) =>
        Array.from({ length: dots }, (_, i) => {
          const y = 12 + (i - (dots - 1) / 2) * 3.4
          return (
            <circle
              key={`${x}-${i}`}
              cx={x}
              cy={y}
              r="1.25"
              fill="currentColor"
            />
          )
        }),
      )}
    </svg>
  )
}

// A brief radial burst of brand pixels — the small celebration for finishing
// the application (the full crown ceremony waits for acceptance).
const BURST_COLORS = ['#C9A84C', '#34d399', '#ffffff', '#d9bd65']

function PixelBurst({ count = 48 }) {
  const [fired, setFired] = useState(false)
  const dotsRef = useRef(null)
  if (!dotsRef.current) {
    dotsRef.current = Array.from({ length: count }, (_, i) => {
      const angle = (i / count) * Math.PI * 2 + (i % 3) * 0.4
      const dist = 60 + (i % 5) * 26
      return {
        x: Math.cos(angle) * dist,
        y: Math.sin(angle) * dist,
        color: BURST_COLORS[i % BURST_COLORS.length],
        delay: (i % 8) * 24,
        size: 3 + (i % 3),
      }
    })
  }
  useEffect(() => {
    const raf = requestAnimationFrame(() => setFired(true))
    return () => cancelAnimationFrame(raf)
  }, [])

  return (
    <span
      aria-hidden
      className="pointer-events-none relative inline-block size-6 motion-reduce:hidden"
    >
      {dotsRef.current.map((d, i) => (
        <span
          key={i}
          style={{
            background: d.color,
            width: d.size,
            height: d.size,
            transform: fired
              ? `translate(${d.x}px, ${d.y}px) scale(0.4)`
              : 'translate(0, 0) scale(1)',
            opacity: fired ? 0 : 1,
            transitionDelay: `${d.delay}ms`,
          }}
          className="absolute top-1/2 left-1/2 rounded-full transition-all duration-1000 ease-out"
        />
      ))}
    </span>
  )
}

// 5x5 dot-matrix check for the what-happens-next timeline.
const TAKEOVER_CHECK = [
  [0, 0, 0, 0, 1],
  [0, 0, 0, 1, 0],
  [1, 0, 1, 0, 0],
  [0, 1, 0, 0, 0],
  [0, 0, 0, 0, 0],
]

function StepPlate({ done }) {
  return (
    <span
      className={`flex shrink-0 flex-col gap-[2px] rounded-full border bg-neutral-950/60 p-[5px] ${
      done
        ? 'border-emerald-400/30 text-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.3)]'
        : 'animate-pulse border-white/10 text-white'
      }`}
      aria-hidden
    >
      {TAKEOVER_CHECK.map((row, r) => (
        <span key={r} className="flex gap-[2px]">
          {row.map((on, c) => (
            <span
              key={c}
              style={{ opacity: done ? (on ? 1 : 0.12) : 0.18 }}
              className="inline-block size-0.5 rounded-full bg-current"
            />
          ))}
        </span>
      ))}
    </span>
  )
}

// The transcript gracefully clears into this once the interview completes:
// pixel-burst, gratitude, and the road ahead. Exported so ApplyFormStepper
// renders the identical thank-you state — same visual, whichever tab
// finished the application (binding contract #6).
export function SubmittedTakeover({ firstName, email }) {
  const steps = [
    { label: 'Application received', done: true },
    { label: 'Our team and committee review your story', done: false },
    { label: "You'll hear from us shortly — either way", done: false },
  ]
  return (
    <div className="mx-auto mt-10 w-full max-w-md">
      <div className="rounded-3xl border border-kingdom-gold/30 bg-kingdom-gold/[0.06] p-8 text-center">
        <div className="flex justify-center pt-4 pb-8">
          <PixelBurst />
        </div>
        <h2 className="font-display text-2xl font-medium">
          Thank you{firstName ? `, ${firstName}` : ''}.
        </h2>
        <p className="mt-3 text-sm leading-6 text-white/70">
          Your application is in. Here&apos;s what happens next:
        </p>
        <ul className="mx-auto mt-6 max-w-xs space-y-3 text-left">
          {steps.map((s) => (
            <li key={s.label} className="flex items-center gap-3 text-sm">
              <StepPlate done={s.done} />
              <span className={s.done ? 'text-white' : 'text-white/60'}>
                {s.label}
              </span>
            </li>
          ))}
        </ul>
        {email && (
          <p className="mt-6 text-xs text-neutral-500">
            A confirmation is on its way to{' '}
            <span className="text-neutral-300">{email}</span>.
          </p>
        )}
      </div>
    </div>
  )
}

// Structured in-chat question (AskUserQuestion-style): a chip-labeled card
// with option buttons instead of free text. Answering sends a natural founder
// message through the normal turn flow PLUS a structured payload the server
// records deterministically — so the URL lands on the application even if the
// model never repeats it.
function WebsiteAsk({ busy, onAnswer }) {
  const [mode, setMode] = useState('choice') // 'choice' | 'url'
  const [url, setUrl] = useState('')
  const [invalid, setInvalid] = useState(false)

  function submitUrl(e) {
    e?.preventDefault()
    const raw = url.trim()
    if (!raw) return
    try {
      const candidate = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`
      const parsed = new URL(candidate)
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') throw new Error()
      if (!parsed.hostname.includes('.')) throw new Error()
      setInvalid(false)
      onAnswer(`Yes — our website is ${parsed.href}`, { has: true, url: parsed.href })
    } catch {
      setInvalid(true)
    }
  }

  return (
    <div className="max-w-[85%] rounded-2xl border border-kingdom-gold/30 bg-kingdom-gold/[0.05] p-4">
      <span className="inline-block rounded-full border border-kingdom-gold/40 px-2.5 py-0.5 font-mono text-[0.6rem] tracking-[0.2em] text-kingdom-gold uppercase">
        Website
      </span>
      <p className="mt-2.5 text-sm leading-relaxed text-white">
        Quick one — do you already have a website for the venture?
      </p>
      {mode === 'choice' ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => setMode('url')}
            className="min-h-11 rounded-full border border-kingdom-gold/40 bg-neutral-950 px-4 py-2 text-sm font-medium text-kingdom-gold transition hover:border-kingdom-gold disabled:opacity-50"
          >
            Yes, we do
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onAnswer("We don't have a website yet.", { has: false })}
            className="min-h-11 rounded-full border border-white/15 bg-neutral-950 px-4 py-2 text-sm font-medium text-neutral-300 transition hover:border-white/40 disabled:opacity-50"
          >
            Not yet
          </button>
        </div>
      ) : (
        <form onSubmit={submitUrl} className="mt-3">
          <div className="flex flex-wrap gap-2">
            <input
              type="text"
              name="website"
              inputMode="url"
              autoComplete="url"
              aria-label="Your website address"
              aria-invalid={invalid || undefined}
              aria-describedby={invalid ? 'website-ask-error' : undefined}
              placeholder="yourcompany.com"
              autoFocus
              value={url}
              onChange={(e) => {
                setUrl(e.target.value)
                setInvalid(false)
              }}
              className="min-h-11 min-w-0 flex-1 rounded-full border border-white/15 bg-neutral-950 px-4 py-2 text-base text-white placeholder:text-neutral-500 focus:border-kingdom-gold/60 focus:outline-none sm:text-sm"
            />
            <button
              type="submit"
              disabled={busy || !url.trim()}
              className="min-h-11 rounded-full border border-kingdom-gold/40 bg-kingdom-gold px-4 py-2 text-sm font-semibold text-neutral-950 transition hover:bg-[#d9bd65] disabled:opacity-50"
            >
              Share it
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setMode('choice')}
              className="min-h-11 rounded-full px-3 py-2 text-sm text-neutral-500 transition hover:text-neutral-300"
            >
              Back
            </button>
          </div>
          {invalid && (
            <p id="website-ask-error" role="alert" className="mt-2 text-xs text-red-400">
              That doesn&apos;t look like a web address — try something like
              yourcompany.com.
            </p>
          )}
        </form>
      )}
    </div>
  )
}

// Generic (non-invited) founders introduce themselves before the interview:
// name + email mint their private resume token, so the application is never
// unrecoverable — and entering the same email later picks the same
// application back up. On success the page reloads into the personalized,
// tokenized session.
function StartForm() {
  const [founderName, setFounderName] = useState('')
  const [email, setEmail] = useState('')
  const [ventureName, setVentureName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  async function begin(e) {
    e.preventDefault()
    if (!founderName.trim() || !email.trim() || busy) return
    setBusy(true)
    setError(null)
    try {
      const res = await fetch('/api/z2h/begin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          founderName: founderName.trim(),
          email: email.trim(),
          ventureName: ventureName.trim() || undefined,
        }),
      })
      const data = await res.json()
      if (!res.ok || !data.ok) throw new Error(data.error || 'Something went wrong')
      window.location.href = `/zero-to-hero/apply?invite=${data.inviteToken}`
    } catch (err) {
      setError(err.message)
      setBusy(false)
    }
  }

  const field =
    'w-full rounded-xl border border-white/10 bg-neutral-950/60 px-4 py-3 text-white placeholder-white/30 outline-none focus:border-kingdom-gold/60'

  return (
    <div className="mx-auto mt-10 w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.03] p-8">
      <h2 className="font-display text-xl font-medium">First, a quick intro</h2>
      <p className="mt-2 text-sm leading-6 text-white/60">
        Your application saves as you go — we&apos;ll email you a private link
        so you can pause and come back any time. Already started? Enter the
        same email to pick up where you left off.
      </p>
      <form onSubmit={begin} className="mt-6 space-y-3">
        <input
          type="text"
          name="name"
          autoComplete="name"
          aria-label="Your full name"
          required
          placeholder="Your name"
          value={founderName}
          onChange={(e) => setFounderName(e.target.value)}
          className={field}
        />
        <input
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          aria-label="Email address"
          required
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={field}
        />
        <input
          type="text"
          name="organization"
          autoComplete="organization"
          aria-label="Venture name (optional)"
          placeholder="Venture name (optional)"
          value={ventureName}
          onChange={(e) => setVentureName(e.target.value)}
          className={field}
        />
        {error && <p className="text-sm text-red-400">{error}</p>}
        <button
          type="submit"
          disabled={busy || !founderName.trim() || !email.trim()}
          className="w-full rounded-full bg-kingdom-gold px-6 py-3.5 font-semibold text-neutral-950 transition hover:bg-[#d9bd65] disabled:opacity-60"
        >
          {busy ? 'Setting things up…' : 'Start my application'}
        </button>
      </form>
    </div>
  )
}

export function ApplyChat({
  founderName = null,
  ventureName = null,
  founderEmail = null,
  initialComplete = false,
  initialRemaining = null,
  initialWebsiteUrl = null,
  interviewStarted = false,
  // Server-fresh { remaining, complete } snapshot from ApplyTabs, refreshed
  // on every tab switch — see the effect below.
  syncedState = null,
}) {
  const params = useSearchParams()
  const inviteToken = params.get('invite') || null

  const startRemaining = Array.isArray(initialRemaining)
    ? initialRemaining
    : [...Z2H_DIMENSIONS]

  // Invited founders get greeted by name, with their venture woven into the
  // opening question. Anonymous visitors get the generic opener — and a
  // founder returning to an unfinished interview gets pointed at exactly
  // what's left instead of being asked to start over.
  const firstName = firstNameOf(founderName)
  const opener =
    interviewStarted && !initialComplete && startRemaining.length > 0 && startRemaining.length < Z2H_DIMENSIONS.length
      ? `Welcome back${firstName ? `, ${firstName}` : ''} — everything you've shared is saved. Still left to cover: ${listLabels(startRemaining)}. Pick up whenever you're ready — type below, or tap the orb to continue by voice.`
      : firstName
        ? `Welcome, ${firstName} — glad you're here. Let's start simple: tell me about yourself, then${ventureName ? ` the story behind ${ventureName}` : " the venture you're building"}. What's the idea? What sparked it?`
        : OPENER

  const [messages, setMessages] = useState([
    { role: 'interviewer', text: opener },
  ])
  const [input, setInput] = useState('')
  const [applicationId, setApplicationId] = useState(null)
  const [busy, setBusy] = useState(false)
  const [complete, setComplete] = useState(Boolean(initialComplete))
  const [remaining, setRemaining] = useState(startRemaining)
  const [error, setError] = useState(null)
  // The structured website answer rides the next turn's body until a turn
  // succeeds — so a failed send + plain-text retry can't lose the URL.
  const [websiteAnswered, setWebsiteAnswered] = useState(Boolean(initialWebsiteUrl))
  const pendingWebsiteRef = useRef(null)

  // Cross-tab coverage sync: answers saved on the Form tab count here too
  // (they steer the voice session's resume point and the "what's left"
  // messaging), and a Form-tab submit flips this tab straight to the
  // thank-you state. Props only seed once at mount; this keeps us live.
  useEffect(() => {
    if (!syncedState) return
    if (Array.isArray(syncedState.remaining)) setRemaining(syncedState.remaining)
    if (syncedState.complete) setComplete(true)
  }, [syncedState])

  // Dictation (speech-to-text into the composer — the founder reviews before
  // sending, exactly like ChatGPT's mic).
  const [dictationSupported, setDictationSupported] = useState(false)
  const [listening, setListening] = useState(false)
  const recognitionRef = useRef(null)
  const dictationBaseRef = useRef('')

  // First-time nudge toward the voice orb — most visitors never notice a
  // plain icon button is a whole conversational mode. Shown once per browser,
  // dismissed by using it, dismissing it, or sending a couple of messages.
  const [showVoiceHint, setShowVoiceHint] = useState(false)
  useEffect(() => {
    try {
      if (!window.localStorage.getItem('z2h_voice_hint_seen')) setShowVoiceHint(true)
    } catch {
      // Private browsing / storage disabled — skip the hint rather than crash.
    }
  }, [])
  function dismissVoiceHint() {
    setShowVoiceHint(false)
    try {
      window.localStorage.setItem('z2h_voice_hint_seen', '1')
    } catch {
      // Ignore — worst case the hint reappears next visit.
    }
  }

  const transcriptRef = useRef(null)
  const inputRef = useRef(null)

  // Auto-size the composer to its content (dictation can pour in whole
  // paragraphs) — grow up to a cap, then scroll inside. Height is restored to
  // its previous value before the new one is committed so the CSS height
  // transition has a start point and the growth animates instead of snapping.
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    const prev = el.style.height
    el.style.height = 'auto'
    const next = `${Math.min(el.scrollHeight, 160)}px`
    el.style.height = prev
    void el.offsetHeight
    el.style.height = next
  }, [input])

  // Conversational mode — live Vapi call presented as a centered modal with
  // the AI orb. The transcript keeps accruing in the chat underneath.
  const vapi = useVapiInterview({
    inviteToken,
    founderName,
    websiteKnown: websiteAnswered,
    onTurn: (turn) =>
      setMessages((m) => {
        // Vapi finalizes speech in fragments — grow the current speaker's
        // bubble instead of stacking fragment bubbles.
        const last = m[m.length - 1]
        if (last?.via === 'voice' && last.role === turn.role) {
          const copy = [...m]
          copy[copy.length - 1] = { ...last, text: `${last.text} ${turn.text}` }
          return copy
        }
        return [...m, { role: turn.role, text: turn.text, via: 'voice' }]
      }),
    onComplete: () => setComplete(true),
    // The call ended with areas still uncovered (deliberate stop or a drop).
    // No scary red text when everything saved — an interviewer bubble names
    // exactly what's left and how to continue, so the path forward is clear.
    onIncomplete: ({ remaining: rem, dropped }) => {
      if (Array.isArray(rem) && rem.length > 0) setRemaining(rem)
      const lead = dropped
        ? 'The call dropped, but everything you said is saved.'
        : 'Good progress — everything you shared is saved.'
      const next =
        Array.isArray(rem) && rem.length > 0
          ? ` Still left to cover: ${listLabels(rem)}. Type your answer below, or tap the orb and we'll pick up right there.`
          : ' Continue below whenever you’re ready.'
      setMessages((m) => [...m, { role: 'interviewer', text: `${lead}${next}` }])
    },
    onError: (msg) => setError(msg),
  })
  const conversationOpen = vapi.callState !== 'idle'

  // Scroll only the transcript panel itself, not the sentinel's full
  // ancestor chain. `scrollIntoView` walks every scroll container between
  // the sentinel and the viewport — including RootLayout's outer wrapper,
  // which is `overflow-hidden` (a valid, JS-scrollable scroll container
  // even without a visible scrollbar). On mobile that ancestor scroll is
  // what actually clipped the composer: the page silently shifted up
  // inside a hidden-overflow box while this viewport-locked screen assumes
  // it never scrolls. Driving the transcript's own scrollTop keeps the
  // scroll fully contained to the bordered panel.
  useEffect(() => {
    transcriptRef.current?.scrollTo({
      top: transcriptRef.current.scrollHeight,
      behavior: 'smooth',
    })
  }, [messages, busy])

  useEffect(() => {
    setDictationSupported(
      Boolean(window.SpeechRecognition || window.webkitSpeechRecognition),
    )
    return () => recognitionRef.current?.abort?.()
  }, [])

  function stopDictation() {
    recognitionRef.current?.stop?.()
  }

  function startDictation() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SR || busy || complete || listening) return
    setError(null)
    dictationBaseRef.current = input ? `${input.trim()} ` : ''

    const recognition = new SR()
    recognition.lang = navigator.language?.startsWith('en')
      ? navigator.language
      : 'en-US'
    recognition.interimResults = true
    recognition.continuous = true

    recognition.onresult = (e) => {
      let text = ''
      for (const result of e.results) text += result[0].transcript
      // Words land in the composer as they're spoken — review, edit, send.
      setInput(`${dictationBaseRef.current}${text}`.slice(0, 2000))
    }
    recognition.onend = () => {
      setListening(false)
      recognitionRef.current = null
    }
    recognition.onerror = (e) => {
      setListening(false)
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        setError(
          'Microphone access was blocked — allow it in your browser, or type instead.',
        )
      } else if (e.error !== 'aborted' && e.error !== 'no-speech') {
        setError('Dictation hiccuped — try again, or type instead.')
      }
    }

    recognitionRef.current = recognition
    setListening(true)
    try {
      recognition.start()
    } catch {
      setListening(false)
    }
  }

  function startConversation() {
    if (showVoiceHint) dismissVoiceHint()
    setError(null)
    stopDictation()
    // A session with history resumes at the first uncovered area instead of
    // greeting the founder from scratch.
    vapi.start({
      remaining,
      resuming: interviewStarted || messages.length > 1,
    })
  }

  async function send(e, overrideMessage) {
    e?.preventDefault()
    if (showVoiceHint) dismissVoiceHint()
    stopDictation()
    const message = (overrideMessage ?? input).trim()
    if (!message || busy || complete) return
    setError(null)
    setBusy(true)
    setMessages((m) => [...m, { role: 'founder', text: message }])
    setInput('')

    try {
      const res = await fetch('/api/z2h/intake', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          ...(inviteToken ? { inviteToken } : {}),
          ...(applicationId ? { applicationId } : {}),
          ...(pendingWebsiteRef.current ? { website: pendingWebsiteRef.current } : {}),
        }),
      })

      // Pre-stream failures (validation, closed application) come back as JSON.
      const contentType = res.headers.get('content-type') || ''
      if (!contentType.includes('text/event-stream')) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || 'Something went wrong')
      }

      // The interviewer's reply streams in live as `delta` events.
      setMessages((m) => [
        ...m,
        { role: 'interviewer', text: '', streaming: true },
      ])
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let pending = ''
      let sawDone = false

      const appendDelta = (text) =>
        setMessages((m) => {
          const copy = [...m]
          const last = copy[copy.length - 1]
          copy[copy.length - 1] = { ...last, text: last.text + text }
          return copy
        })

      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        pending += decoder.decode(value, { stream: true })
        const events = pending.split('\n\n')
        pending = events.pop()
        for (const raw of events) {
          const line = raw.trim()
          if (!line.startsWith('data: ')) continue
          const evt = JSON.parse(line.slice(6))
          if (evt.type === 'delta') appendDelta(evt.text)
          else if (evt.type === 'error') throw new Error(evt.error)
          else if (evt.type === 'done') {
            sawDone = true
            pendingWebsiteRef.current = null
            setApplicationId(evt.applicationId)
            if (Array.isArray(evt.remaining)) setRemaining(evt.remaining)
            // Reconcile with the authoritative final reply.
            setMessages((m) => {
              const copy = [...m]
              copy[copy.length - 1] = { role: 'interviewer', text: evt.reply }
              return copy
            })
            if (evt.complete) setComplete(true)
          }
        }
      }
      if (!sawDone)
        throw new Error('Connection interrupted — please try again.')
    } catch (err) {
      setError(err.message)
      // Give the founder their message back so nothing is lost.
      setInput(message)
      setMessages((m) => {
        let copy = [...m]
        if (copy[copy.length - 1]?.streaming) copy = copy.slice(0, -1)
        if (copy[copy.length - 1]?.role === 'founder') copy = copy.slice(0, -1)
        return copy
      })
    } finally {
      setBusy(false)
    }
  }

  // No token = not started yet. Capture name + email first so the
  // application is recoverable from message one.
  // The page shell is viewport-locked (h-[calc(100dvh-…)]) for the chat's
  // pinned-composer layout, so terminal states must provide their own scroll
  // container — otherwise short mobile viewports clip their bottom edge.
  if (!inviteToken) {
    return (
      <div className="min-h-0 flex-1 overflow-y-auto pb-4">
        <StartForm />
      </div>
    )
  }

  // Interview complete — the transcript clears into the thank-you moment.
  if (complete) {
    return (
      <div className="min-h-0 flex-1 overflow-y-auto pb-4">
        <SubmittedTakeover firstName={firstName} email={founderEmail} />
      </div>
    )
  }

  return (
    <div className="mt-6 flex min-h-0 flex-1 flex-col">
      {/* Transcript scrolls internally; the composer below stays pinned. */}
      <div
        ref={transcriptRef}
        className="min-h-0 flex-1 space-y-4 overflow-y-auto rounded-3xl border border-white/10 bg-white/[0.03] p-5 sm:p-7"
      >
        {messages
          .filter((m) => m.text || !m.streaming)
          .map((m, i) => (
            <div
              key={i}
              className={
                m.role === 'founder' ? 'flex justify-end' : 'flex justify-start'
              }
            >
              <div
                className={
                  m.role === 'founder'
                    ? 'max-w-[85%] min-w-0 rounded-2xl rounded-br-sm bg-kingdom-gold/90 px-4 py-3 text-sm leading-relaxed break-words text-neutral-950'
                    : 'max-w-[85%] min-w-0 rounded-2xl rounded-bl-sm bg-white/8 px-4 py-3 text-sm leading-relaxed break-words text-neutral-100'
                }
              >
                {m.text}
                {m.streaming && m.text ? (
                  <span className="ml-0.5 inline-block h-3.5 w-[2px] animate-pulse bg-kingdom-gold align-middle" />
                ) : null}
              </div>
            </div>
          ))}
        {inviteToken && !websiteAnswered && !busy && (
          <div className="flex justify-start">
            <WebsiteAsk
              busy={busy}
              onAnswer={(text, website) => {
                setWebsiteAnswered(true)
                pendingWebsiteRef.current = website
                send(null, text)
              }}
            />
          </div>
        )}
        {busy && !messages[messages.length - 1]?.text && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-sm bg-white/8 px-4 py-3 text-sm text-neutral-400">
              <span className="inline-flex gap-1">
                <span className="animate-pulse">·</span>
                <span className="animate-pulse [animation-delay:150ms]">·</span>
                <span className="animate-pulse [animation-delay:300ms]">·</span>
              </span>
            </div>
          </div>
        )}
      </div>

      {error && (
        <p className="mt-3 text-center text-sm text-red-400">{error}</p>
      )}

      {/* The composer — ChatGPT-style: the text spans the field's full width
          and wraps fluidly; the actions live in their own row underneath,
          floated to the bottom right. The field grows with its content. */}
      {!complete && (
        <form
          onSubmit={send}
          className="mt-4 rounded-[28px] border border-white/15 bg-white/5 px-2 pt-2 pb-2 transition focus-within:border-kingdom-gold/60"
        >
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              // Enter sends, Shift+Enter breaks a line — same as the big chat apps.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                send(e)
              }
            }}
            disabled={busy}
            maxLength={2000}
            rows={1}
            placeholder={
              listening
                ? 'Listening — your words appear here…'
                : 'Write your answer…'
            }
            className="block max-h-40 w-full resize-none overflow-y-auto bg-transparent px-3 py-2 text-base leading-6 text-white transition-[height] duration-150 ease-[cubic-bezier(.645,.045,.355,1)] placeholder:text-neutral-500 focus:outline-none disabled:opacity-50 motion-reduce:transition-none sm:text-sm"
          />
          <div className="mt-1 flex flex-wrap items-center justify-end gap-1.5">
            {dictationSupported && (
              <button
                type="button"
                onClick={listening ? stopDictation : startDictation}
                disabled={busy}
                aria-label={
                  listening ? 'Stop dictation' : 'Dictate your answer'
                }
                title="Dictate"
                className={
                  listening
                    ? 'relative flex size-11 shrink-0 items-center justify-center rounded-full bg-red-500/90 text-white transition after:absolute after:inset-0 after:animate-ping after:rounded-full after:bg-red-500/40'
                    : 'flex size-11 shrink-0 items-center justify-center rounded-full text-neutral-300 transition hover:bg-white/10 hover:text-kingdom-gold disabled:opacity-40'
                }
              >
                <MicIcon className="size-5" />
              </button>
            )}
            {vapi.available && (
              <span className="relative">
                {showVoiceHint && (
                  <div
                    role="tooltip"
                    className="absolute right-0 bottom-full z-20 mb-3 w-52 max-w-[calc(100vw-2rem)] animate-in fade-in slide-in-from-bottom-1 rounded-2xl border border-kingdom-gold/30 bg-neutral-900 p-3 text-left shadow-[0_12px_40px_rgba(0,0,0,0.5)] duration-300"
                  >
                    <button
                      type="button"
                      onClick={dismissVoiceHint}
                      aria-label="Dismiss tip"
                      className="absolute -top-1 -right-1 flex size-8 items-center justify-center rounded-full text-neutral-500 transition hover:text-neutral-300"
                    >
                      ✕
                    </button>
                    <p className="pr-4 text-xs leading-relaxed text-neutral-200">
                      <span className="font-semibold text-kingdom-gold">New:</span> tap the orb
                      to talk through your application out loud instead of typing.
                    </p>
                    <span
                      aria-hidden
                      className="absolute -bottom-1.5 right-4 size-3 rotate-45 border-r border-b border-kingdom-gold/30 bg-neutral-900"
                    />
                  </div>
                )}
                <button
                  type="button"
                  onClick={startConversation}
                  disabled={busy}
                  aria-label="Start a voice conversation"
                  title="Voice conversation"
                  className={
                    showVoiceHint
                      ? 'group relative flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-full border border-kingdom-gold bg-neutral-950 shadow-[0_0_0_4px_rgba(201,168,76,0.15)] transition hover:border-kingdom-gold disabled:opacity-40'
                      : 'group relative flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-full border border-kingdom-gold/40 bg-neutral-950 transition hover:border-kingdom-gold disabled:opacity-40'
                  }
                >
                  {/* The orb idles behind the icon — always alive. */}
                  <span className="pointer-events-none absolute inset-0">
                    <Orb
                      hue={45}
                      hoverIntensity={1.6}
                      rotateOnHover
                      backgroundColor="#0a0a0a"
                    />
                  </span>
                  <DotWaveIcon className="pointer-events-none relative z-10 size-5 text-white drop-shadow-[0_0_6px_rgba(0,0,0,0.9)] transition-colors group-hover:text-kingdom-gold" />
                </button>
              </span>
            )}
            <button
              type="submit"
              disabled={busy || !input.trim()}
              aria-label="Send"
              title="Send"
              className={
                input.trim()
                  ? 'flex size-11 shrink-0 items-center justify-center rounded-full bg-kingdom-gold text-white transition hover:bg-kingdom-gold/90 disabled:opacity-40'
                  : 'pointer-events-none flex size-11 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 text-neutral-400'
              }
            >
              <SendDotsIcon ready={Boolean(input.trim())} />
            </button>
          </div>
        </form>
      )}

      {/* Conversational mode — the AI orb takes center stage. Dismissing ends
          the session and returns to the transcript (dictate, type, or restart
          the conversation any time). */}
      {conversationOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/80 pt-[max(1rem,env(safe-area-inset-top))] pr-[max(1rem,env(safe-area-inset-right))] pb-[max(1rem,env(safe-area-inset-bottom))] pl-[max(1rem,env(safe-area-inset-left))] backdrop-blur-md"
          onClick={vapi.callState === 'finalizing' ? undefined : vapi.stop}
        >
          <div
            className="relative mx-4 flex max-h-full w-full max-w-sm flex-col items-center overflow-y-auto rounded-3xl border border-white/10 bg-neutral-900/90 px-8 pt-10 pb-8 text-center shadow-[0_40px_120px_rgba(0,0,0,0.6)]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Finalization takes a few seconds and MUST land — hide the exit
                so it can't be mistaken for a frozen screen with a way out. */}
            {vapi.callState !== 'finalizing' && (
              <button
                type="button"
                onClick={vapi.stop}
                aria-label="End conversation and view transcript"
                className="absolute top-3 right-3 flex size-11 items-center justify-center rounded-full border border-white/15 text-sm text-neutral-400 transition hover:border-red-400/50 hover:text-red-300"
              >
                ✕
              </button>
            )}

            <div className="relative size-48 sm:size-56">
              <Orb
                hue={45}
                hoverIntensity={2.4}
                rotateOnHover
                forceHoverState={vapi.assistantSpeaking}
                backgroundColor="#171717"
              />
            </div>

            <p className="mt-6 font-display text-lg text-white">
              {vapi.callState === 'connecting'
                ? 'Connecting…'
                : vapi.callState === 'finalizing'
                  ? 'Wrapping up your application…'
                  : vapi.assistantSpeaking
                    ? 'Interviewer speaking…'
                    : vapi.muted
                      ? 'Muted — tap the mic to speak'
                      : 'Listening — go ahead'}
            </p>

            {/* Live caption of whoever is talking right now — or, while
                finalizing, visible activity so the wrap-up never reads as a
                frozen screen. */}
            <p className="mt-3 min-h-10 text-sm leading-relaxed text-neutral-400 italic">
              {vapi.callState === 'finalizing' ? (
                <span className="not-italic">
                  Saving your answers, checking every question is covered
                  <span className="ml-1 inline-flex gap-0.5">
                    <span className="animate-pulse">·</span>
                    <span className="animate-pulse [animation-delay:150ms]">·</span>
                    <span className="animate-pulse [animation-delay:300ms]">·</span>
                  </span>
                </span>
              ) : vapi.liveCaption?.text ? (
                `${vapi.liveCaption.text}…`
              ) : (
                ''
              )}
            </p>

            {/* Mic mute — step away, cough, think out loud in private. */}
            <button
              type="button"
              onClick={vapi.toggleMute}
              aria-label={vapi.muted ? 'Unmute microphone' : 'Mute microphone'}
              aria-pressed={vapi.muted}
              title={vapi.muted ? 'Unmute' : 'Mute'}
              className={
                vapi.muted
                  ? 'mt-4 flex size-12 items-center justify-center rounded-full bg-red-500/90 text-white transition hover:bg-red-500'
                  : 'mt-4 flex size-12 items-center justify-center rounded-full border border-white/15 text-neutral-300 transition hover:border-kingdom-gold hover:text-kingdom-gold'
              }
            >
              {vapi.muted ? (
                <MicOffIcon className="size-5" />
              ) : (
                <MicIcon className="size-5" />
              )}
            </button>

            <p className="mt-4 text-[0.68rem] text-neutral-500">
              Talk naturally — the full transcript is saved to your application.
              Close this window any time to read it or answer by text.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
